import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Image, Alert, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo1 from '../../../assets/logo1.png';
import logo2 from '../../../assets/logo2.png';
import loginImg from '../../../assets/logo1.png';
import bg from '../../../assets/bg.jpg';
import { LoginService } from '../../../services/login.services';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!email.trim()) errors.email = 'Email is required';
    if (!password) errors.password = 'Password is required';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Object.values(errors).forEach(error => toast.error(error));
      return;
    }
    
    setLoading(true);
    setFormErrors({});
    
    try {
      const result = await LoginService(email, password);
      
      if (result.success) {
        toast.success('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else {
        toast.error(result.error || 'Login failed');
        setFormErrors({ general: result.error });
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundImage: `url(${bg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed'
    }}>
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      
      {/* Overlay for better readability */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 0
      }} />
      
      <header className="d-flex align-items-center justify-content-between px-4 py-3" style={{ position: 'relative', zIndex: 2 }}>
        <div className="d-flex align-items-center gap-3">
          <Image src={logo1} height={50} alt="Logo 1" style={{ objectFit: 'contain' }} />
          <Image src={logo2} height={50} alt="Logo 2" style={{ objectFit: 'contain' }} />
        </div>
      </header>

      <Container fluid className="d-flex align-items-center justify-content-center py-5 flex-grow-1 position-relative" style={{ zIndex: 1 }}>
        <Card style={{
          width: '100%',
          maxWidth: '1000px',
          borderRadius: '20px',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0 15px 35px rgba(0, 0, 0, 0.5)'
        }}>
          <Card.Body className="p-0">
            <Row className="g-0">
              <Col md={6} className="d-none d-md-flex align-items-center justify-content-center p-4">
                <div style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '500px',
                  backgroundImage: `url(${loginImg})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  borderRadius: '10px'
                }} />
              </Col>

              <Col xs={12} md={6} className="d-flex align-items-center justify-content-center p-4 p-md-5">
                <div style={{ width: '100%', maxWidth: '380px' }}>
                  <div className="text-center mb-4">
                    <h1 className="h2 mb-3 text-white" style={{ fontWeight: 600 }}>Welcome Back</h1>
                    <p className="text-light" style={{ opacity: 0.9 }}>Please sign in to your account</p>
                  </div>

                  {formErrors.general && <Alert variant="danger" className="mb-3">{formErrors.general}</Alert>}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="text-white mb-2" style={{ fontWeight: 500 }}>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="py-2 px-3"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          border: `1px solid ${formErrors.email ? '#dc3545' : 'rgba(255, 255, 255, 0.3)'}`,
                          color: '#fff',
                          borderRadius: '8px'
                        }}
                        isInvalid={!!formErrors.email}
                      />
                      <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="text-white mb-2" style={{ fontWeight: 500 }}>Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="py-2 px-3"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          border: `1px solid ${formErrors.password ? '#dc3545' : 'rgba(255, 255, 255, 0.3)'}`,
                          color: '#fff',
                          borderRadius: '8px'
                        }}
                        isInvalid={!!formErrors.password}
                      />
                      <Form.Control.Feedback type="invalid">{formErrors.password}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="d-flex justify-content-between align-items-center mb-4">
                      <Form.Check
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        label={<span className="text-white" style={{ opacity: 0.9 }}>Remember me</span>}
                      />
                      <a 
                        href="/forgot-password" 
                        className="text-decoration-none" 
                        style={{ color: '#4dabf7', fontSize: '0.9rem', fontWeight: 500 }}
                        onClick={(e) => {
                          e.preventDefault();
                          toast.info('Forgot password feature coming soon!');
                        }}
                      >
                        Forgot Password?
                      </a>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 py-2 mb-3"
                      disabled={loading}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        borderRadius: '8px',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : 'Sign In'}
                    </Button>
                  </Form>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      <footer className="text-center py-3 position-relative" style={{ zIndex: 2 }}>
        <p className="text-light mb-0" style={{ fontSize: '0.9rem', opacity: 0.9 }}>
          &copy; {new Date().getFullYear()} RONAQ BANQUETS. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Login;